import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { outreachAction } from '@/lib/outreach';
import type { OutreachParameter } from '@shared/outreach';

interface OutreachParametersProps {
  toolAppId: string;
  parameters: OutreachParameter[];
  canManage: boolean;
  onInsert: (key: string) => void;
  onChanged: () => void;
}

const GROUPS: Array<{ from: OutreachParameter['from']; label: string }> = [
  { from: 'list', label: 'Liste' },
  { from: 'person', label: 'Personne' },
  { from: 'sender', label: 'Expéditeur' },
];

/**
 * The parameters an e-mail can use (lemlist's variable picker): a click inserts
 * {{key}} where the cursor is. The list's own parameters are its columns; one
 * added here is a new column of the list, filled by hand or by another app.
 */
export const OutreachParameters = ({ toolAppId, parameters, canManage, onInsert, onChanged }: OutreachParametersProps) => {
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const act = (body: Record<string, unknown>) => outreachAction(body)
    .then(() => { setError(null); onChanged(); })
    .catch((cause: unknown) => setError(cause instanceof Error ? cause.message : 'Action impossible.'));

  return (
    <div className="space-y-3 rounded-xl border border-domino-cardBorder bg-domino-white p-3">
      {GROUPS.map((group) => (
        <div key={group.from} className="space-y-1.5">
          <p className="text-xs font-medium text-domino-inkSoft">{group.label}</p>
          <div className="flex flex-wrap gap-1.5">
            {parameters.filter((parameter) => parameter.from === group.from).map((parameter) => (
              <span key={parameter.key} className="inline-flex items-center rounded-lg bg-domino-warm text-xs text-domino-ink">
                <button type="button" title={`{{${parameter.key}}}`} onMouseDown={(event) => event.preventDefault()} onClick={() => onInsert(parameter.key)}
                  className="rounded-lg px-2 py-1 hover:bg-domino-plumTint hover:text-domino-aubergine">
                  {parameter.label}
                </button>
                {parameter.manual && canManage && (
                  <button type="button" aria-label={`Retirer ${parameter.label}`} onClick={() => { void act({ action: 'remove_parameter', toolAppId, key: parameter.key }); }}
                    className="pr-1.5 text-domino-inkSoft hover:text-domino-aubergine">
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            ))}
            {group.from === 'list' && canManage && !adding && (
              <button type="button" onClick={() => setAdding(true)} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-domino-inkSoft hover:text-domino-aubergine">
                <Plus className="h-3 w-3" /> Paramètre
              </button>
            )}
          </div>
          {group.from === 'list' && adding && (
            <form className="flex gap-1.5" onSubmit={(event) => {
              event.preventDefault();
              void act({ action: 'add_parameter', toolAppId, label }).then(() => { setAdding(false); setLabel(''); });
            }}>
              <input autoFocus required value={label} onChange={(event) => setLabel(event.target.value)} aria-label="Nom du paramètre"
                className="min-w-0 flex-1 rounded-lg border border-domino-cardBorder px-2 py-1 text-xs text-domino-ink focus:border-domino-aubergine focus:outline-none" />
              <button type="submit" className="rounded-lg bg-domino-aubergine px-2 py-1 text-xs font-semibold text-white">Ajouter</button>
            </form>
          )}
        </div>
      ))}
      {error && <p role="alert" className="text-xs text-domino-inkSoft">{error}</p>}
    </div>
  );
};
